import i18n from 'i18next'
import { meSetLang } from '@/shared/api/generated/clients/meSetLang'
import { fallbackLanguage, normalizeLanguage, persistLanguage, type Language } from './language'

const languagePersistenceDebounceMs = 1000

let explicitLanguageVersion = 0
let languagePersistenceTimer: ReturnType<typeof setTimeout> | undefined
let pendingLanguagePersistence: { language: Language; version: number } | undefined
let pendingExplicitLanguageVersion: number | undefined
let languagePersistenceChain = Promise.resolve()
const fallbackUsers = new Set<string>()

const clearLanguagePersistenceTimer = () => {
  if (languagePersistenceTimer === undefined) {
    return
  }

  clearTimeout(languagePersistenceTimer)
  languagePersistenceTimer = undefined
  pendingLanguagePersistence = undefined
}

const applyLanguage = (language: Language) => {
  persistLanguage(language)
  void i18n.changeLanguage(language)
}

const persistLanguageOnServer = (language: Language, version: number, isExplicit = false) => {
  languagePersistenceChain = languagePersistenceChain.then(async () => {
    if (version !== explicitLanguageVersion) {
      return
    }

    try {
      const { data } = await meSetLang({
        body: { lang: language },
        contentType: 'application/json-patch+json',
      })
      const effectiveLanguage = normalizeLanguage(data.result)

      if (effectiveLanguage && version === explicitLanguageVersion) {
        applyLanguage(effectiveLanguage)
      }
    } catch {
    } finally {
      if (isExplicit && pendingExplicitLanguageVersion === version) {
        pendingExplicitLanguageVersion = undefined
      }
    }
  })

  return languagePersistenceChain
}

const scheduleLanguagePersistence = (language: Language, version: number) => {
  clearLanguagePersistenceTimer()
  pendingLanguagePersistence = { language, version }
  languagePersistenceTimer = setTimeout(() => {
    languagePersistenceTimer = undefined
    const pending = pendingLanguagePersistence
    pendingLanguagePersistence = undefined

    if (!pending || pending.version !== explicitLanguageVersion) {
      return
    }

    void persistLanguageOnServer(pending.language, pending.version, true)
  }, languagePersistenceDebounceMs)
}

export const getLanguageVersion = () => explicitLanguageVersion

export const changeLanguagePreference = (language: Language, persistOnServer: boolean) => {
  explicitLanguageVersion += 1
  persistLanguage(language)
  void i18n.changeLanguage(language)

  if (!persistOnServer) {
    pendingExplicitLanguageVersion = undefined
    clearLanguagePersistenceTimer()
    return
  }

  pendingExplicitLanguageVersion = explicitLanguageVersion
  scheduleLanguagePersistence(language, explicitLanguageVersion)
}

export const syncLanguageFromServer = (
  languageValue: unknown,
  userKey: string,
  version: number
) => {
  if (version !== explicitLanguageVersion) {
    return
  }

  if (pendingExplicitLanguageVersion === version) {
    return
  }

  const serverLanguage = normalizeLanguage(languageValue)
  if (serverLanguage) {
    applyLanguage(serverLanguage)
    return
  }

  applyLanguage(fallbackLanguage)

  if (fallbackUsers.has(userKey)) {
    return
  }

  fallbackUsers.add(userKey)
  void persistLanguageOnServer(fallbackLanguage, version)
}

export const cancelLanguagePersistence = () => {
  explicitLanguageVersion += 1
  pendingExplicitLanguageVersion = undefined
  clearLanguagePersistenceTimer()
}
